import type { TargetProperty } from "@ernestoagg/valuation-engine";

export type NewTasacionField =
  | "address"
  | "propertyType"
  | "rooms"
  | "totalM2"
  | "coveredM2"
  | "semiCoveredM2"
  | "uncoveredM2"
  | "parking"
  | "parkingType"
  | "surfaces";

export type NewTasacionErrors = Partial<Record<NewTasacionField, string>>;

const numberFormatter = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 2 });

function isNonNegativeNumber(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value >= 0;
}

export function surfaceBalanceMessage(target: TargetProperty): string | undefined {
  if (![target.totalM2, target.coveredM2, target.semiCoveredM2, target.uncoveredM2].every(isNonNegativeNumber)) return undefined;
  const total = target.totalM2!;
  const classified = target.coveredM2! + target.semiCoveredM2! + target.uncoveredM2!;
  const difference = total - classified;
  if (Math.abs(difference) < 0.011) return undefined;
  const totalText = numberFormatter.format(total);
  const classifiedText = numberFormatter.format(classified);
  const differenceText = numberFormatter.format(Math.abs(difference));
  return difference > 0
    ? `Faltan ${differenceText} m² por distribuir: las superficies informadas suman ${classifiedText} m² y el total es ${totalText} m².`
    : `Sobran ${differenceText} m²: las superficies informadas suman ${classifiedText} m² y el total es ${totalText} m².`;
}

export function validateNewTasacion(target: TargetProperty): NewTasacionErrors {
  const errors: NewTasacionErrors = {};
  if (target.address.trim().length < 3) errors.address = "Ingresá una dirección de al menos 3 caracteres.";
  if (!target.propertyType.trim()) errors.propertyType = "Seleccioná el tipo de propiedad.";
  if (!target.rooms || target.rooms <= 0) errors.rooms = "Ingresá una cantidad de ambientes mayor que 0.";
  if (!isNonNegativeNumber(target.totalM2)) errors.totalM2 = "Ingresá los m² totales.";
  if (!isNonNegativeNumber(target.coveredM2)) errors.coveredM2 = "Ingresá los m² cubiertos. Si no tiene, ingresá 0.";
  if (!isNonNegativeNumber(target.semiCoveredM2)) errors.semiCoveredM2 = "Ingresá los m² semicubiertos. Si no tiene, ingresá 0.";
  if (!isNonNegativeNumber(target.uncoveredM2)) errors.uncoveredM2 = "Ingresá los m² descubiertos. Si no tiene, ingresá 0.";
  const surfaceError = surfaceBalanceMessage(target);
  if (surfaceError) errors.surfaces = surfaceError;
  if (!target.parking) errors.parking = "Indicá si la propiedad tiene cochera.";
  if (target.parking === "yes" && !target.parkingType) errors.parkingType = "Seleccioná el tipo de cochera.";
  return errors;
}
